// @flow
import * as React from 'react';

import type { StaticHandlerArgs } from '@gandi/app-runner/server';
import type { DecoratedProduct, Product } from '@gandi/cart';
import { sendPage } from '@gandi/tracking';
import type { TranslatorProps } from '@gandi/translate';
import { Spinner, Steps, Step, Alert } from '@gandi/ui';

import type { FormComponentProps } from 'gandi.react-form';

import { Intro } from '../../Layout';
import { Layout } from '../../components/Layout';

import { handleMarketingParams } from '../../modules/app/actions';
import type { StaticHandlerArgsWithMarketing } from '../../modules/app/actions';
import type { Account } from '../../modules/cloud/resources/accounts';
import type {
  FullState,
  PreFetchArgs,
  PreFetchReturn,
  WithRouterProps,
} from '../../types';

import type { AnyQuery } from '../../types/query';

import { preFetchAccount } from '../modules/account';

import type { FetchAccountParams } from '../modules/account';

import { preFetchServers } from '../modules/servers';

import Form from './components/Form';
import StepConfirmation from './components/StepConfirmation';
import StepServer from './components/StepServer';
import StepSettings from './components/StepSettings';

import styles from './Presenter.scss';

type Slug = string;
type FormRef = {
  values: {
    +[field: string]: mixed,
  },
};

export type PresenterStateProps = {
  cartId: string,
  cloudProducts: Array<DecoratedProduct>,
  formValues: { +[field: string]: mixed, ... },
  isFirstServer: boolean,
  product: ?Product,
};

export type Params = {
  cartId: string,
  step: string,
  productId: string,
};

export type PresenterInjectedProps = {
  account: Account,
  fetchAccountParams: FetchAccountParams,
  isFailureAccount: boolean,
  isFetchingAccount: boolean,
  isFetchingServers: boolean,
  isLoadedAccount: boolean,
  isLoadedServers: boolean,
  translator: TranslatorProps,
  ...WithRouterProps<Params, AnyQuery>,
};

export type PresenterProps = {
  ...PresenterStateProps,
  ...PresenterInjectedProps,
};

export type ChildProps = {
  ...PresenterProps,
  form: FormComponentProps,
  getValues: () => { ... } | void,
};

export type StepsMap = Array<{
  slug: Slug,
  title: string,
  component: React.ComponentType<{ ... }>,
}>;

export default class Presenter extends React.Component<PresenterProps> {
  stepsMap: StepsMap;
  form: ?FormRef;

  constructor(props: PresenterProps) {
    super(props);

    const {
      translator: { __ },
    } = this.props;
    this.stepsMap = [
      {
        title: __('Server'),
        slug: 'server',
        component: StepServer,
      },
      {
        title: __('Settings'),
        slug: 'settings',
        component: StepSettings,
      },
      {
        title: __('Confirmation'),
        slug: 'confirmation',
        component: StepConfirmation,
      },
    ];
  }

  static async handleRedirect(props: {
    ...StaticHandlerArgsWithMarketing<>,
    ...StaticHandlerArgs<{}, Params, { ... }, FullState>,
  }): Promise<void> {
    if (
      props.params.step &&
      (!props.params.cartId || !props.params.productId)
    ) {
      props.redirect('/cloud/create');
      const error: Error = new Error(
        'No product id or cart id, redirecting to cloud creation page.',
      );
      throw error;
    }

    // $FlowFixMe
    await handleMarketingParams(props);
  }

  static preFetch(
    args: PreFetchArgs<{}, void, empty, FullState>,
  ): PreFetchReturn<> {
    return Promise.allSettled([preFetchAccount(args), preFetchServers(args)]);
  }

  componentDidMount() {
    if (
      // $FlowFixMe[sketchy-number-and]
      this.props.cloudProducts.length &&
      (!this.props.params.productId ||
        this.props.cloudProducts.filter(
          product => `${product.id}` !== this.props.params.productId,
        ).length)
    ) {
      this.props.navigate(
        `/cloud/create/server/${this.props.cartId}/${this.props.cloudProducts[0].id}`,
        { replace: true },
      );
    }

    const currentStep = this.stepsMap[this.currentStepIndex];
    sendPage({
      chapter1: 'Shop',
      chapter2: 'Hosting',
      chapter3: 'cloud',
      name: currentStep.slug,
    });
  }

  UNSAFE_componentWillReceiveProps(nextProps: PresenterProps) {
    if (
      nextProps.params.step !== this.props.params.step &&
      !!this.props.params.step
    ) {
      const newStep = nextProps.params.step;
      sendPage({
        chapter1: 'Shop',
        chapter2: 'Hosting',
        chapter3: 'cloud',
        name: newStep,
      });
    }
  }

  formRef: (ref: ?FormRef) => void = (ref: ?FormRef) => {
    this.form = ref;
  };

  getValues: () => ?any = () => this.form && this.form.values;

  get currentStepIndex(): number {
    const currentStepIndex = this.stepsMap.indexOf(
      this.stepsMap.filter(step => step.slug === this.props.params.step)[0],
    );

    return currentStepIndex > -1 ? currentStepIndex : 0;
  }

  renderFormChild: (form: FormComponentProps) => React.Node = form => {
    const Component = this.stepsMap[this.currentStepIndex].component;
    return <Component {...this.props} getValues={this.getValues} form={form} />;
  };

  decorateContent(...content: Array<React.Node>): React.Node {
    const { translator } = this.props;
    const { __ } = translator;

    const currentStep = this.currentStepIndex + 1;

    return (
      <Layout lockedOrga>
        <Intro icon="hosting" title={__('Buy a new Cloud server')} />
        {!this.props.isFetchingAccount && (
          <Steps active={currentStep} marginBottom="base">
            <Step>{this.stepsMap[0].title}</Step>
            <Step>{this.stepsMap[1].title}</Step>
            <Step>{this.stepsMap[2].title}</Step>
          </Steps>
        )}
        <div className={styles.formContainer}>
          {React.Children.toArray(content)}
        </div>
      </Layout>
    );
  }

  render(): React.Node {
    const { translator } = this.props;

    if (
      (!this.props.isLoadedAccount || !this.props.isLoadedServers) &&
      (this.props.isFetchingAccount || this.props.isFetchingServers)
    ) {
      return this.decorateContent(<Spinner centered />);
    }

    if (!this.props.isLoadedAccount && this.props.isFailureAccount) {
      return this.decorateContent(
        <Alert
          intent="danger"
          message={translator.__('Unexpected error occurred')}
        />,
      );
    }

    return this.decorateContent(
      <Form
        ref={this.formRef}
        render={this.renderFormChild}
        // Pass `product` prop to compute initial values if needed.
        product={this.props.product}
      />,
    );
  }
}
